/**
 * Exponential Backoff Retry Utility
 *
 * การหน่วงเวลาแบบ Exponential:
 * - Retry 1: 1s (2^0 * 1000ms)
 * - Retry 2: 2s (2^1 * 1000ms)
 * - Retry 3: 4s (2^2 * 1000ms)
 * - Retry 4: 8s (2^3 * 1000ms)
 * - Retry 5: 16s (2^4 * 1000ms, จำกัดไม่เกิน maxDelay)
 */

const DEFAULT_MAX_RETRIES = 5;
const DEFAULT_BASE_DELAY = 1000; // 1 second
const DEFAULT_MAX_DELAY = 10000; // 10 seconds

/**
 * Simple Request Queue - ป้องกันยิง API พร้อมกันมากเกินไป
 */
class RequestQueue {
  constructor(delayBetweenRequests = 150) {
    this.queue = [];
    this.processing = false;
    this.delayBetweenRequests = delayBetweenRequests;
  }

  async enqueue(requestFn) {
    return new Promise((resolve, reject) => {
      this.queue.push({ requestFn, resolve, reject });
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.processing || this.queue.length === 0) return;
    this.processing = true;

    while (this.queue.length > 0) {
      const { requestFn, resolve, reject } = this.queue.shift();
      try {
        const result = await requestFn();
        resolve(result);
      } catch (error) {
        reject(error);
      }
      if (this.queue.length > 0) {
        await new Promise(r => setTimeout(r, this.delayBetweenRequests));
      }
    }
    this.processing = false;
  }
}

const apiQueue = new RequestQueue(150); // 150ms delay ระหว่าง request

/**
 * Fetch with exponential backoff retry + request queuing
 * @param {string} url - URL to fetch
 * @param {object} options - Fetch options
 * @param {number} maxRetries - Maximum retry attempts (default: 5)
 * @param {number} baseDelay - Base delay in ms (default: 1000)
 * @param {number} maxDelay - Max delay cap in ms (default: 10000)
 * @returns {Promise<Response>}
 */
export async function fetchWithRetry(
  url,
  options = {},
  maxRetries = DEFAULT_MAX_RETRIES,
  baseDelay = DEFAULT_BASE_DELAY,
  maxDelay = DEFAULT_MAX_DELAY
) {
  // ใช้ queue สำหรับ GET requests เท่านั้น (POST/PUT/DELETE ไม่ queue)
  const method = (options.method || 'GET').toUpperCase();
  const shouldQueue = method === 'GET';

  const executeRequest = async () => {
    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, options);

        // อ่านค่า Retry-After header จาก server (ถ้ามี)
        const retryAfter = response.headers.get('Retry-After');

        // ถ้าโดน rate limit (429) และยังมี retry เหลือ
        if (response.status === 429 && attempt < maxRetries) {
          let delay;

          // ถ้า server บอกให้รอกี่วินาที ใช้ค่านั้น
          if (retryAfter) {
            // Retry-After อาจเป็นตัวเลข (วินาที) หรือ HTTP date
            const retryAfterSeconds = parseInt(retryAfter, 10);
            delay = isNaN(retryAfterSeconds)
              ? baseDelay // ถ้าเป็น date ใช้ค่า default
              : Math.min(retryAfterSeconds * 1000, maxDelay); // แปลงเป็น ms และจำกัดไม่เกิน maxDelay
          } else {
            // ใช้ exponential backoff: 2^attempt * baseDelay
            delay = Math.min(Math.pow(2, attempt) * baseDelay, maxDelay);
          }

          console.warn(
            `Rate limited (429) - Retry ${attempt + 1}/${maxRetries} after ${delay}ms`,
            retryAfter ? `(Server suggested: ${retryAfter})` : '(Exponential backoff)'
          );

          await new Promise(resolve => setTimeout(resolve, delay));
          continue; // ลองใหม่
        }

        // ถ้าได้ response กลับมา (ไม่ว่าจะ success หรือ error อื่นๆ) return ทันที
        return response;

      } catch (error) {
        lastError = error;

        // ถ้ายังมี retry เหลือ
        if (attempt < maxRetries) {
          const delay = Math.min(Math.pow(2, attempt) * baseDelay, maxDelay);
          console.warn(
            `Network error - Retry ${attempt + 1}/${maxRetries} after ${delay}ms:`,
            error.message
          );
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // ถ้า retry หมดแล้วยังไม่สำเร็จ throw error
    throw lastError || new Error('Max retries exceeded');
  };

  // ถ้าเป็น GET request ให้ใช้ queue, ไม่งั้นยิงตรงๆ
  if (shouldQueue) {
    return apiQueue.enqueue(executeRequest);
  } else {
    return executeRequest();
  }
}

/**
 * Helper function สำหรับ GET request with JSON response
 */
export async function fetchJSON(url, options = {}, retryConfig = {}) {
  const response = await fetchWithRetry(
    url,
    options,
    retryConfig.maxRetries,
    retryConfig.baseDelay,
    retryConfig.maxDelay
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return await response.json();
}
